/**
 * MONSTER TOWN — WHEN THE GAME SPEAKS. VOICE CREW (src/voice/**).
 *
 * DATA. Every row is "this happened -> say this". A new line for an existing
 * moment is a row here plus a row in `src/content/voiceLines.json`; nothing in
 * `src/voice/index.ts` knows what any of these keys mean.
 *
 * THIS CREW READS THE BUS AND NOTHING ELSE. It imports `core/events` (and the
 * expansion's event declarations) and no crew's module, in either direction —
 * so wiring voice onto a moment can never change what that moment does. That is
 * LAW #4 enforced by construction: if this whole folder were deleted the game
 * would play exactly the same, silently.
 *
 * WHY SOME ROWS MATCH ON A SOUND CUE OR A PARTICLE COUNT.
 * The kitchen, the zoo trough and the fishing rod do not publish "the kid put a
 * wrong thing in the pot" — they publish the reaction to it (`audio:cue` +
 * `fx:play`), because until now nobody needed the moment by name. Adding an
 * event to those files would be editing three other crews' code for a feature
 * that must not be able to affect them, so this crew listens to what they
 * already say instead. The matches are scoped as tightly as the payloads allow
 * (the panel that is open, the exact particle count) and every one of them is
 * asserted live by `npm run voice-check`, which drives the real game and
 * requires the key to come out — so a crew that changes its cue breaks a named
 * check here rather than quietly going quiet.
 */

import type { EventName, GameEvents } from '../core/events'

/** A row: on `on`, if `when` says so, say `say`. */
export interface VoiceTrigger<K extends EventName = EventName> {
  readonly id: string
  readonly on: K
  /** Optional guard. `ctx` carries the small amount of state this crew keeps. */
  readonly when?: (payload: GameEvents[K], ctx: TriggerContext) => boolean
  /** The key to say, or null to say nothing. */
  readonly say: string | ((payload: GameEvents[K], ctx: TriggerContext) => string | null)
  /**
   * Lower numbers win when two lines land together. A tutorial beat outranks a
   * chatty flourish, because the beat is the one a kid is waiting on.
   */
  readonly priority?: number
}

/** Any row, with its own event's payload type still attached. */
export type AnyVoiceTrigger = { [K in EventName]: VoiceTrigger<K> }[EventName]

/**
 * Write a row through this and `payload` is typed as that event's payload —
 * `e.beatId` on a `tutorial:beat` row is a compile error on a `time:tick` one.
 */
function trigger<K extends EventName>(cfg: VoiceTrigger<K>): AnyVoiceTrigger {
  return cfg as AnyVoiceTrigger
}

export interface TriggerContext {
  /** Which HUD panel is open, as published on `ui:panel`. */
  readonly panel: string | null
  /** Neighbour names learned from `npc:movedIn`, by npc id. */
  nameOf(npcId: string): string | null
}

/** `mt.tut.<beatId>` — the five beats of the first run, by their own ids. */
const TUTORIAL_BEATS = new Set(['walk', 'use', 'build', 'place', 'home'])

/** Recipes that have a line of their own. A new recipe falls back to done. */
const RECIPE_LINE = new Set([
  'recipe_fruit_bowl',
  'recipe_pancakes',
  'recipe_veg_stew',
  'recipe_berry_jam',
  'recipe_honey_cake',
  'recipe_berry_juice',
])

/** Errands whose giver has a recorded "here is what I need" line. */
const ERRAND_ASK = new Set([
  'errand_bench_pond',
  'errand_three_flowers',
  'errand_path_home',
  'errand_red_roof',
  'errand_two_trees',
  'errand_fence_yard',
])

/** Neighbours with recorded lines of their own, by lower-case name. */
const NEIGHBOUR_LINE = new Set([
  'bramble',
  'pip',
  'moss',
  'tuffet',
  'dot',
  'nimbus',
  'sorrel',
  'bubble',
  'fen',
  'wobble',
])

function neighbourKey(name: string | null, suffix: 'hello' | 'thanks'): string | null {
  if (name === null) return null
  const n = name.toLowerCase()
  return NEIGHBOUR_LINE.has(n) ? `mt.npc.${n}.${suffix}` : null
}

export const VOICE_TRIGGERS: readonly AnyVoiceTrigger[] = [
  // -- hello, and who you are ----------------------------------------------
  // The first real gesture is the first moment the audio hardware is awake, so
  // it is the earliest point at which a welcome can actually be heard.
  trigger({ id: 'welcome', on: 'audio:unlocked', say: 'mt.welcome', priority: 3 }),
  trigger({ id: 'welcome.back', on: 'save:slotChanged', say: 'mt.welcomeBack', priority: 3 }),
  trigger({ id: 'monster.created', on: 'monster:created', say: 'mt.monster.created', priority: 2 }),

  // -- the first run --------------------------------------------------------
  trigger({ id: 'tut.start', on: 'tutorial:started', say: 'mt.tut.start', priority: 0 }),
  trigger({
    id: 'tut.beat',
    on: 'tutorial:beat',
    say: (e) => (TUTORIAL_BEATS.has(e.beatId) ? `mt.tut.${e.beatId}` : null),
    priority: 0,
  }),
  trigger({ id: 'tut.undo', on: 'build:undo', say: 'mt.tut.undo', priority: 1 }),
  trigger({ id: 'tut.done', on: 'tutorial:finished', say: 'mt.tut.done', priority: 0 }),

  // -- the clock ------------------------------------------------------------
  trigger({ id: 'day.phase', on: 'time:phaseChanged', say: (e) => `mt.day.${e.phase}`, priority: 4 }),
  trigger({ id: 'day.new', on: 'time:dayStarted', say: 'mt.day.newDay', priority: 4 }),
  trigger({ id: 'day.slept', on: 'time:slept', say: 'mt.sleep.morning', priority: 1 }),

  // -- the two gentle invitations (never a warning — LAW #2) ----------------
  trigger({ id: 'need.invited', on: 'need:invited', say: (e) => `mt.need.${e.id}`, priority: 2 }),
  trigger({ id: 'need.satisfied', on: 'need:satisfied', say: 'mt.need.happy', priority: 2 }),

  // -- errands --------------------------------------------------------------
  trigger({ id: 'errand.offered', on: 'errand:offered', say: 'mt.errand.offered', priority: 1 }),
  trigger({
    id: 'errand.granted',
    on: 'errand:granted',
    // The neighbour's own ask if they have one; otherwise the narrator explains
    // the card. (The reference errand says its own line from its step data.)
    say: (e) =>
      ERRAND_ASK.has(e.id) ? `mt.${e.id.replace(/^errand_/, 'errand.')}.ask` : 'mt.errand.granted',
    priority: 1,
  }),
  trigger({
    id: 'errand.card',
    on: 'errand:cardHidden',
    when: (e) => !e.hidden,
    say: 'mt.errand.card',
    priority: 2,
  }),
  trigger({ id: 'errand.step', on: 'errand:stepDone', say: 'mt.errand.step', priority: 1 }),
  trigger({ id: 'errand.progress', on: 'errand:progressed', say: 'mt.errand.progress', priority: 3 }),
  trigger({ id: 'errand.finished', on: 'errand:finished', say: 'mt.errand.finished', priority: 0 }),

  // -- neighbours -----------------------------------------------------------
  trigger({
    id: 'npc.movedIn',
    on: 'npc:movedIn',
    say: (e) => neighbourKey(e.name, 'hello') ?? 'mt.npc.movedIn',
    priority: 2,
  }),
  trigger({
    id: 'npc.visited',
    on: 'npc:visited',
    say: (e, c) => neighbourKey(c.nameOf(e.npcId), 'hello') ?? 'mt.npc.visited',
    priority: 3,
  }),
  trigger({
    id: 'npc.admired',
    on: 'npc:admired',
    say: (e, c) => neighbourKey(c.nameOf(e.npcId), 'thanks') ?? 'mt.npc.admired',
    priority: 3,
  }),
  trigger({ id: 'npc.gift', on: 'npc:leftGift', say: 'mt.npc.gift', priority: 2 }),

  // -- the shop and the purse ----------------------------------------------
  trigger({ id: 'shop.welcome', on: 'shop:offer', say: 'mt.shop.welcome', priority: 2 }),
  trigger({ id: 'shop.bought', on: 'shop:bought', say: 'mt.shop.bought', priority: 2 }),
  trigger({ id: 'shop.tooDear', on: 'shop:tooDear', say: 'mt.shop.tooDear', priority: 1 }),
  trigger({ id: 'shop.unlocked', on: 'economy:unlocked', say: 'mt.shop.unlocked', priority: 1 }),
  trigger({
    id: 'coins.big',
    on: 'economy:earned',
    when: (e) => e.amount >= 20,
    say: 'mt.shop.coins',
    priority: 3,
  }),

  // -- the kitchen ----------------------------------------------------------
  // The cook sheet publishes its reactions, not its moments; `panel` scopes them
  // to the kitchen so 'giggle' in the zoo cannot answer for the pot.
  trigger({ id: 'cook.open', on: 'ui:panel', when: (e) => e.panel === 'cook', say: 'mt.cook.open', priority: 2 }),
  trigger({ id: 'cook.pick', on: 'audio:cue', when: (e, c) => c.panel === 'cook' && e.cue === 'ui_tap', say: 'mt.cook.pick', priority: 3 }),
  trigger({ id: 'cook.add', on: 'audio:cue', when: (e, c) => c.panel === 'cook' && e.cue === 'pop_soft', say: 'mt.cook.add', priority: 2 }),
  trigger({ id: 'cook.wrong', on: 'audio:cue', when: (e, c) => c.panel === 'cook' && e.cue === 'giggle', say: 'mt.cook.wrong', priority: 1 }),
  trigger({ id: 'cook.need', on: 'audio:cue', when: (e, c) => c.panel === 'cook' && e.cue === 'nope_soft', say: 'mt.cook.need', priority: 1 }),
  trigger({
    id: 'cook.stir',
    on: 'audio:cue',
    when: (e, c) => c.panel === 'cook' && (e.cue === 'ladle' || e.cue === 'ding_ready'),
    say: 'mt.cook.stir',
    priority: 2,
  }),
  trigger({
    id: 'cook.done',
    on: 'kitchen:cooked',
    say: (e) =>
      RECIPE_LINE.has(e.recipeId)
        ? `mt.cook.recipe.${e.recipeId.replace(/^recipe_/, '')}`
        : 'mt.cook.done',
    priority: 0,
  }),

  // -- the zoo --------------------------------------------------------------
  trigger({
    id: 'zoo.welcome',
    on: 'scene:entered',
    when: (e) => String(e.scene) === 'venue_zoo',
    say: 'mt.zoo.welcome',
    priority: 2,
  }),
  trigger({
    id: 'zoo.hungry',
    on: 'ui:toast',
    when: (e) => e.icon === 'dinner',
    say: 'mt.zoo.hungry',
    priority: 1,
  }),
  trigger({ id: 'zoo.right', on: 'audio:cue', when: (e, c) => c.panel === 'zoofeed' && e.cue === 'chime_happy', say: 'mt.zoo.right', priority: 1 }),
  trigger({ id: 'zoo.wrong', on: 'audio:cue', when: (e, c) => c.panel === 'zoofeed' && e.cue === 'giggle', say: 'mt.zoo.wrong', priority: 1 }),
  trigger({
    id: 'zoo.fed',
    on: 'errand:observe',
    when: (e) => e.kind === 'deliver' && e.to === 'zoo_feeder',
    say: 'mt.zoo.fed',
    priority: 0,
  }),
  trigger({
    id: 'zoo.animal',
    on: 'play:interacted',
    say: (e) =>
      String(e.defId).startsWith('zoo_animal_')
        ? `mt.zoo.animal.${String(e.defId).slice('zoo_animal_'.length)}`
        : null,
    priority: 2,
  }),

  // -- fishing, bugs and the book ------------------------------------------
  // `src/catching/fishing.ts` is the only place in the game that asks for a
  // 'point' pose of exactly 0.8 s (the cast) or a splash of exactly 16 or 6
  // particles (the bite, and the fish swimming off). Documented at the top.
  trigger({
    id: 'catch.cast',
    on: 'monster:action',
    when: (e) => e.action === 'point' && e.seconds === 0.8,
    say: 'mt.catch.cast',
    priority: 2,
  }),
  trigger({
    id: 'catch.bite',
    on: 'fx:play',
    when: (e) => e.effect === 'splash' && e.count === 16,
    say: 'mt.catch.bite',
    priority: 0,
  }),
  trigger({
    id: 'catch.miss',
    on: 'fx:play',
    when: (e) => e.effect === 'splash' && e.count === 6,
    say: 'mt.catch.miss',
    priority: 1,
  }),
  trigger({
    id: 'catch.found',
    on: 'collection:found',
    say: (e) => (e.firstTime ? 'mt.catch.newFind' : e.kind === 'bug' ? 'mt.catch.bug' : 'mt.catch.caught'),
    priority: 0,
  }),
  trigger({ id: 'catch.book', on: 'ui:panel', when: (e) => e.panel === 'book', say: 'mt.catch.book', priority: 2 }),

  // -- your brother's town --------------------------------------------------
  trigger({ id: 'visit.enter', on: 'visit:entered', say: 'mt.visit.enter', priority: 0 }),
  trigger({ id: 'visit.leave', on: 'visit:left', say: 'mt.visit.leave', priority: 1 }),
  trigger({ id: 'visit.photo', on: 'ui:photoTaken', say: 'mt.visit.photo', priority: 2 }),
  trigger({ id: 'visit.giftLeft', on: 'gift:left', say: 'mt.visit.giftLeft', priority: 1 }),
  trigger({ id: 'visit.giftGot', on: 'gift:received', say: 'mt.visit.giftGot', priority: 0 }),

  // -- the one toggle -------------------------------------------------------
  trigger({
    id: 'mode',
    on: 'app:modeChanged',
    say: (e) => (e.mode === 'build' ? 'mt.build.mode' : 'mt.build.play'),
    priority: 4,
  }),
]

/**
 * Every key any row above can produce, including the ones behind a branch.
 * `voice-check` compares this against the manifest in both directions, which is
 * what "every sayKey resolves, and no clip is an orphan" actually means.
 */
export const TRIGGER_KEYS: readonly string[] = [
  'mt.welcome',
  'mt.welcomeBack',
  'mt.monster.created',
  'mt.tut.start',
  ...[...TUTORIAL_BEATS].map((b) => `mt.tut.${b}`),
  'mt.tut.undo',
  'mt.tut.done',
  'mt.day.dawn',
  'mt.day.day',
  'mt.day.dusk',
  'mt.day.night',
  'mt.day.newDay',
  'mt.sleep.morning',
  'mt.need.hungry',
  'mt.need.sleepy',
  'mt.need.happy',
  'mt.errand.offered',
  'mt.errand.granted',
  'mt.errand.card',
  'mt.errand.step',
  'mt.errand.progress',
  'mt.errand.finished',
  ...[...ERRAND_ASK].map((id) => `mt.${id.replace(/^errand_/, 'errand.')}.ask`),
  ...[...NEIGHBOUR_LINE].flatMap((n) => [`mt.npc.${n}.hello`, `mt.npc.${n}.thanks`]),
  'mt.npc.movedIn',
  'mt.npc.visited',
  'mt.npc.admired',
  'mt.npc.gift',
  'mt.shop.welcome',
  'mt.shop.bought',
  'mt.shop.tooDear',
  'mt.shop.unlocked',
  'mt.shop.coins',
  'mt.cook.open',
  'mt.cook.pick',
  'mt.cook.add',
  'mt.cook.wrong',
  'mt.cook.need',
  'mt.cook.stir',
  'mt.cook.done',
  ...[...RECIPE_LINE].map((r) => `mt.cook.recipe.${r.replace(/^recipe_/, '')}`),
  'mt.zoo.welcome',
  'mt.zoo.hungry',
  'mt.zoo.right',
  'mt.zoo.wrong',
  'mt.zoo.fed',
  ...['hoppy', 'splash', 'munch', 'tiny', 'stripe', 'climb'].map((a) => `mt.zoo.animal.${a}`),
  'mt.catch.cast',
  'mt.catch.bite',
  'mt.catch.miss',
  'mt.catch.caught',
  'mt.catch.bug',
  'mt.catch.newFind',
  'mt.catch.book',
  'mt.visit.enter',
  'mt.visit.leave',
  'mt.visit.photo',
  'mt.visit.giftLeft',
  'mt.visit.giftGot',
  'mt.build.mode',
  'mt.build.play',
]
